import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  background: color-mix(in srgb, var(--brand-navy, #00212d) 52%, transparent);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: ${fadeIn} 0.2s ease-out;
`;

export const ModalContent = styled.div`
  position: relative;
  background: var(--surface-1);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  padding: 1.8rem;
  width: 100%;
  max-width: 480px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  transition: transform 0.2s ease;
  animation: ${slideUp} 0.25s cubic-bezier(0.2, 0.9, 0.4, 1.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--brand-cta-gradient-135);
  }
  
  @media (max-width: 560px) {
    padding: 1.4rem;
    border-radius: 16px;
  }
`;


export const ModalHeader = styled.h3`
  margin: 0 0 6px 0;
  font-size: 1.35rem;
  font-weight: 600;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.2px;
`;

export const ModalBody = styled.div`
  margin-top: 0.5rem;
`;

export const ModalDescription = styled.p`
  margin: 0 0 1.2rem 0;
  font-size: 0.85rem;
  color: var(--text-secondary);
  line-height: 1.4;
`;


export const ModalTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  background: var(--surface-overlay);
  border: 1.5px solid var(--border-subtle);
  border-radius: 18px;
  color: var(--text-primary);
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);

  &:focus {
    outline: none;
    border-color: var(--text-heading);
    box-shadow: 0 0 0 2px var(--accent-soft);
    background: var(--surface-overlay);
  }

  &::placeholder {
    color: var(--text-muted);
    font-size: 0.85rem;
  }
`;


export const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 1.8rem;
`;

export const ModalButtonBase = styled.button`
  padding: 8px 20px;
  border-radius: 40px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  border: 1.5px solid transparent;

  &:active {
    transform: scale(0.97);
  }
`;

export const ModalButtonCancel = styled(ModalButtonBase)`
  background: var(--surface-glass);
  border-color: var(--border-subtle);
  color: var(--text-secondary);

  &:hover {
    background: var(--surface-glass-strong);
    border-color: var(--text-muted);
    color: var(--text-on-accent);
  }
`;

export const ModalButtonConfirm = styled(ModalButtonBase)`
  background: color-mix(in srgb, var(--danger) 20%, transparent);
  border-color: color-mix(in srgb, var(--danger) 20%, transparent);
  color: var(--danger);

  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--danger) 35%, transparent);
    border-color: var(--danger);
    color: var(--danger);
    box-shadow: 0 2px 8px color-mix(in srgb, var(--danger) 30%, transparent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;